import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@agentsaas/ui";
import type { Channel } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { useT } from "../app/i18n";
import { isEvent, useChannel } from "../data/realtime";
import { FilesStore, baseName } from "./store";

const Ctx = createContext<FilesStore | null>(null);

/** 每个 agent 一个 FilesStore；订阅 agent:<aid>:fs，刷新文件树并通知已打开的文件 */
export function FilesProvider({ aid, children }: { aid: string; children: ReactNode }) {
  const { api } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const store = useMemo(() => new FilesStore(api, aid), [api, aid]);
  useEffect(
    () =>
      store.onEvent((e) => {
        if (e.type === "reloaded") toast.show({ message: t("fs.reloaded", { name: baseName(e.path) }), tone: "accent" });
        if (e.type === "saved") toast.show({ message: t("fs.saved", { name: baseName(e.path) }), tone: "success", duration: 2000 });
      }),
    [store, toast, t],
  );
  useChannel(`agent:${aid}:fs` as Channel, {
    onEvent(e) {
      if (!isEvent(e, "fs.changed")) return;
      void qc.invalidateQueries({ queryKey: ["fs", aid, "tree"] });
      void store.onFsChanged(e.data);
    },
    onGap() {
      void qc.invalidateQueries({ queryKey: ["fs", aid, "tree"] });
    },
  });
  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

export function useFilesStore(): FilesStore {
  const s = useContext(Ctx);
  if (!s) throw new Error("FilesProvider missing");
  return s;
}

export function useFiles() {
  const store = useFilesStore();
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return { store, ...snap, activeFile: snap.files.find((f) => f.path === snap.active) ?? null };
}
